import { FC, useState } from 'react'
import { Spin } from '@pepper/ui'
import {
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from 'use-query-params'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { useGetTopOrganicKeywordsList } from 'container/SEOTools/hooks/useGetTopOrganicKeywordsList'
import { getColumns, getListColumns } from './columns'
import { LARGE_PAGE_LIMIT, SMALL_PAGE_LIMIT } from '../constants'
import StatsWrapper from '../StatsWrapper'
import Table from '../Table'
import {
  DateRange,
  Device,
  ListType,
  Tab,
  TopOrganicKeywordsDistributionApi,
} from '../types'

type Props = {
  domainId: string
  viewFullPage: boolean
  period: DateRange
  date: string
  showNonBranded: boolean
  distributionResponse: {
    data?: TopOrganicKeywordsDistributionApi
    isLoading: boolean
  }
  deviceCategory?: Device
  handleToggle: () => void
  handleViewMore: (list?: ListType) => void
}

export const TopOrganicKeywords: FC<Props> = ({
  domainId,
  handleViewMore,
  date,
  period,
  viewFullPage,
  deviceCategory,
  handleToggle,
  showNonBranded,
  distributionResponse,
}) => {
  const companyId = useSelectedCompanyId()
  const [tab, setTab] = useState<Tab>('DISTRIBUTED')
  const [limit, setLimit] = useState(SMALL_PAGE_LIMIT)
  const [insights] = useQueryParam(
    'insights',
    withDefault(StringParam, undefined)
  )
  const [page, setPage] = useQueryParam('page', withDefault(NumberParam, 1))

  const showInsights = insights === 'true'

  const { organicKeywordsList, isLoading: isListLoading } =
    useGetTopOrganicKeywordsList({
      companyId,
      domainId,
      date,
      period,
      page,
      limit,
      ...(showNonBranded && { queryType: 'non_branded' }),
      isEnabled:
        tab === 'LIST' && !!domainId && !!companyId && !!period && !!date,
      device_category: deviceCategory,
    })

  const handleTabChange = (value: Tab) => {
    setTab(value)
  }

  const handleLimitUpdation = () => {
    viewFullPage ? handleViewMore() : handleViewMore('KEYWORDS')

    !viewFullPage ? setLimit(LARGE_PAGE_LIMIT) : setLimit(SMALL_PAGE_LIMIT)
    viewFullPage && setPage(1)
  }

  return (
    <StatsWrapper
      headerText="Top Organic Keywords"
      handleTabChange={handleTabChange}
      activeTab={tab}
      handleViewMore={handleLimitUpdation}
      showBack={viewFullPage}
      showSwitch={true}
      handleToggle={handleToggle}
      showNonBranded={showNonBranded}
    >
      <Spin
        spinning={
          (domainId?.length ?? 0) > 0 &&
          (tab === 'DISTRIBUTED'
            ? distributionResponse.isLoading
            : isListLoading)
        }
      >
        {tab === 'DISTRIBUTED' ? (
          <Table
            data={distributionResponse?.data?.data?.distribution}
            columns={getColumns()}
            insights={distributionResponse?.data?.data?.insights}
            showFooter={showInsights}
          />
        ) : (
          <Table
            data={organicKeywordsList?.data}
            columns={getListColumns()}
            insights={distributionResponse?.data?.data?.insights}
            showFooter={!viewFullPage && showInsights}
            pagination={
              viewFullPage
                ? {
                    onChange: pageNumber => setPage(pageNumber),
                    hideOnSinglePage: true,
                    position: ['bottomCenter'],
                    pageSize: limit,
                    total: organicKeywordsList?.total,
                    showSizeChanger: false,
                    current: page,
                  }
                : false
            }
          />
        )}
      </Spin>
    </StatsWrapper>
  )
}
